import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { Modal, ConfirmModal, Spinner } from '../components/ui';
import { fmt, fmtQ } from '../utils/fmt';

export default function PeriodoDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [periodo, setPeriodo] = useState(null);
  const [metas, setMetas] = useState([]);
  const [postos, setPostos] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [tab, setTab] = useState('metas');
  const [loading, setLoading] = useState(true);

  // Vendas
  const [vendas, setVendas] = useState([]);
  const [vendasTotal, setVendasTotal] = useState(0);
  const [filterPosto, setFilterPosto] = useState('');
  const [filterFunc, setFilterFunc] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Produtos especiais inline
  const [produtosEspeciais, setProdutosEspeciais] = useState([]);
  const [showProdModal, setShowProdModal] = useState(false);
  const [prodVenda, setProdVenda] = useState(null);
  const [prodForm, setProdForm] = useState({ comissao_frentista: '', comissao_trocador: '', comissao_gerente: '' });
  const [savingProd, setSavingProd] = useState(false);
  const [prodError, setProdError] = useState('');

  // Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Inserção manual de vendas
  const linhaVazia = () => ({ funcionario: '', tipo: 'frentista', produto: '', quantidade: 1, valor_unitario: '', valor_final: '' });
  const [showVendaManual, setShowVendaManual] = useState(false);
  const [vendaManualPosto, setVendaManualPosto] = useState('');
  const [vendaManualLinhas, setVendaManualLinhas] = useState([linhaVazia()]);
  const [savingVendaManual, setSavingVendaManual] = useState(false);
  const [vendaManualErro, setVendaManualErro] = useState('');

  // Meta modal
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [metaForm, setMetaForm] = useState({ posto_id: '', meta_frentista: '', meta_trocador: '', meta_posto: '' });
  const [editMeta, setEditMeta] = useState(null);
  const [savingMeta, setSavingMeta] = useState(false);

  // Funcionário modal
  const [showFuncModal, setShowFuncModal] = useState(false);
  const [funcForm, setFuncForm] = useState({ posto_id: '', nome: '', tipo: 'trocador' });
  const [editFunc, setEditFunc] = useState(null);
  const [savingFunc, setSavingFunc] = useState(false);
  const [deleteFunc, setDeleteFunc] = useState(null);
  const [funcError, setFuncError] = useState('');

  // Desqualificados
  const [todosFuncionarios, setTodosFuncionarios] = useState([]);
  const [desqualificados, setDesqualificados] = useState([]);
  const [loadingDesq, setLoadingDesq] = useState(false);
  const [filterDesqPosto, setFilterDesqPosto] = useState('');
  const [motivoModal, setMotivoModal] = useState(null);
  const [motivoTexto, setMotivoTexto] = useState('');

  // Excluir período
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Metas automáticas (trimestre)
  const [allPeriodos, setAllPeriodos]         = useState([]);
  const [fonteIds, setFonteIds]               = useState([]);
  const [percentualAuto, setPercentualAuto]   = useState(5);
  const [previewRows, setPreviewRows]         = useState(null);
  const [nomesPeriodos, setNomesPeriodos]     = useState({});
  const [loadingAutoMeta, setLoadingAutoMeta] = useState(false);
  const [applyingAutoMeta, setApplyingAutoMeta] = useState(false);
  const [autoMetaResult, setAutoMetaResult]   = useState(null);
  const [erroAutoMeta, setErroAutoMeta]       = useState('');

  const isAdmin = user?.role === 'admin';

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/periodos/${id}`),
      axios.get(`${API}/periodos/${id}/metas`),
      axios.get(`${API}/postos`),
      axios.get(`${API}/periodos/${id}/funcionarios`),
    ]).then(([r1, r2, r3, r4]) => {
      setPeriodo(r1.data);
      setMetas(r2.data);
      setPostos(r3.data);
      setFuncionarios(r4.data);
    }).finally(() => setLoading(false));
  }, [id]);

  const loadVendas = useCallback(() => {
    const params = { page, limit: pageSize };
    if (filterPosto) params.posto_id = filterPosto;
    if (filterFunc) params.funcionario = filterFunc;
    axios.get(`${API}/periodos/${id}/vendas`, { params }).then(r => {
      setVendas(r.data.data);
      setVendasTotal(r.data.total);
    });
  }, [id, page, pageSize, filterPosto, filterFunc]);

  const loadProdutosEspeciais = useCallback(() => {
    if (!postos.length) return;
    Promise.all(postos.map(p => axios.get(`${API}/postos/${p.id}/produtos-especiais`)))
      .then(results => {
        const all = results.flatMap((r, i) => r.data.map(pe => ({ ...pe, posto_codigo: postos[i].codigo })));
        setProdutosEspeciais(all);
      });
  }, [postos]);

  const loadTodosFuncionarios = useCallback(() => {
    setLoadingDesq(true);
    axios.get(`${API}/periodos/${id}/todos-funcionarios`)
      .then(r1 => {
        setTodosFuncionarios(r1.data);
        return axios.get(`${API}/periodos/${id}/desqualificados`).catch(() => ({ data: [] }));
      })
      .then(r2 => setDesqualificados(r2.data))
      .finally(() => setLoadingDesq(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'vendas') { loadVendas(); loadProdutosEspeciais(); } }, [tab, loadVendas, loadProdutosEspeciais]);
  useEffect(() => { if (tab === 'desqualificados') loadTodosFuncionarios(); }, [tab, loadTodosFuncionarios]);
  useEffect(() => {
    if (tab === 'metas-auto' && !allPeriodos.length) {
      axios.get(`${API}/periodos`).then(r => {
        const outros = r.data.filter(p => String(p.id) !== String(id));
        setAllPeriodos(outros);
        // pré-seleciona os 3 mais recentes
        setFonteIds(outros.slice(0, 3).map(p => p.id));
      });
    }
  }, [tab, id, allPeriodos.length]);

  // ── Metas automáticas
  const calcAutoPreview = async () => {
    if (!fonteIds.length) return;
    setLoadingAutoMeta(true); setErroAutoMeta(''); setPreviewRows(null); setAutoMetaResult(null);
    try {
      const r = await axios.get(`${API}/metas-trimestre/preview`, {
        params: { periodo_ids: fonteIds.join(','), percentual: percentualAuto },
      });
      setPreviewRows(r.data.resultado.map(row => ({ ...row })));
      setNomesPeriodos(r.data.nomesPeriodos || {});
    } catch (e) {
      setErroAutoMeta(e.response?.data?.error || 'Erro ao calcular preview');
    } finally { setLoadingAutoMeta(false); }
  };

  const editPreviewRow = (postoId, field, val) => {
    setPreviewRows(prev => prev.map(r =>
      r.posto_id === postoId ? { ...r, [field]: val } : r
    ));
  };

  const aplicarAutoMetas = async (notificarWpp) => {
    if (!previewRows?.length) return;
    setApplyingAutoMeta(true); setAutoMetaResult(null);
    try {
      const metas = previewRows.map(r => ({
        posto_id:       r.posto_id,
        codigo:         r.codigo,
        posto_nome:     r.posto_nome,
        meta_frentista: parseFloat(r.nova_frentista) || 0,
        meta_trocador:  parseFloat(r.nova_trocador)  || 0,
        meta_posto:     parseFloat(r.nova_posto)     || 0,
        whatsapp_group_id: r.whatsapp_group_id || null,
      }));
      const r = await axios.post(`${API}/metas-trimestre/aplicar-valores`, {
        periodo_destino_id: Number(id),
        metas,
        notificar_whatsapp: notificarWpp,
      });
      setAutoMetaResult({ ok: true, data: r.data });
      load(); // recarrega metas do período
    } catch (e) {
      setAutoMetaResult({ ok: false, msg: e.response?.data?.error || 'Erro ao aplicar metas' });
    } finally { setApplyingAutoMeta(false); }
  };

  // ── Venda manual
  const saveVendaManual = async () => {
    if (!vendaManualPosto) { setVendaManualErro('Selecione um posto'); return; }
    const linhasValidas = vendaManualLinhas.filter(l =>
      l.funcionario.trim() && l.produto.trim() && parseFloat(l.valor_final) > 0
    );
    if (!linhasValidas.length) {
      setVendaManualErro('Preencha ao menos uma linha com funcionário, produto e valor final');
      return;
    }
    setSavingVendaManual(true); setVendaManualErro('');
    try {
      await axios.post(`${API}/periodos/${id}/vendas/manual`, {
        vendas: linhasValidas.map(l => ({
          posto_id:       vendaManualPosto,
          funcionario:    l.funcionario.trim(),
          tipo_funcionario: l.tipo,
          produto:        l.produto.trim(),
          quantidade:     parseFloat(l.quantidade) || 1,
          valor_unitario: parseFloat(l.valor_unitario) || 0,
          valor_bruto:    parseFloat(l.valor_final) || 0,
          valor_desconto: 0,
          valor_acrescimo: 0,
          valor_final:    parseFloat(l.valor_final) || 0,
        })),
      });
      setShowVendaManual(false);
      setVendaManualPosto('');
      setVendaManualLinhas([linhaVazia()]);
      loadVendas();
    } catch (e) {
      setVendaManualErro(e.response?.data?.error || 'Erro ao inserir vendas');
    } finally { setSavingVendaManual(false); }
  };

  const deleteVenda = async (vendaId) => {
    if (!window.confirm('Remover esta venda?')) return;
    try {
      await axios.delete(`${API}/periodos/${id}/vendas/${vendaId}`);
      loadVendas();
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao remover venda');
    }
  };

  // ── Import CSV
  const doImportCsv = async (files) => {
    setImporting(true); setImportResult(null);
    const fd = new FormData();
    for (const f of files) fd.append('arquivo', f);
    try {
      const r = await axios.post(`${API}/periodos/${id}/importar-csv`, fd);
      setImportResult({ ok: true, msg: r.data.message });
      load();
      if (tab === 'vendas') loadVendas();
    } catch (e) {
      setImportResult({ ok: false, msg: e.response?.data?.error || 'Erro ao importar' });
    } finally { setImporting(false); }
  };

  // ── Excluir período
  const doDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/periodos/${id}`);
      navigate('/periodos');
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao excluir período');
      setDeleting(false);
    }
  };

  // ── Metas
  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await axios.post(`${API}/periodos/${id}/metas`, metaForm);
      setShowMetaModal(false); load();
    } catch (e) { alert(e.response?.data?.error || 'Erro'); }
    finally { setSavingMeta(false); }
  };

  // ── Funcionários
  const saveFunc = async () => {
    setSavingFunc(true); setFuncError('');
    try {
      if (funcForm.tipo === 'ambos') {
        await axios.post(`${API}/periodos/${id}/funcionarios`, { posto_id: funcForm.posto_id, nome: funcForm.nome, tipo: 'gerente' });
        await axios.post(`${API}/periodos/${id}/funcionarios`, { posto_id: funcForm.posto_id, nome: funcForm.nome, tipo: 'trocador' });
        if (editFunc && editFunc.tipo !== 'gerente' && editFunc.tipo !== 'trocador') {
          await axios.delete(`${API}/periodos/${id}/funcionarios/${editFunc.id}`);
        }
      } else if (editFunc) {
        await axios.put(`${API}/periodos/${id}/funcionarios/${editFunc.id}`, { nome: funcForm.nome, tipo: funcForm.tipo });
      } else {
        await axios.post(`${API}/periodos/${id}/funcionarios`, { posto_id: funcForm.posto_id, nome: funcForm.nome, tipo: funcForm.tipo });
      }
      setShowFuncModal(false);
      load();
    } catch (e) {
      setFuncError(e.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSavingFunc(false);
    }
  };

  const doDeleteFunc = async () => {
    await axios.delete(`${API}/periodos/${id}/funcionarios/${deleteFunc.id}`);
    setDeleteFunc(null); load();
  };

  // ── Desqualificados
  const isDesqualificado = (f) =>
    desqualificados.some(d =>
      d.posto_id === f.posto_id &&
      d.nome.trim().toLowerCase() === f.nome.trim().toLowerCase() &&
      d.tipo === f.tipo
    );

  const getDesqRecord = (f) =>
    desqualificados.find(d =>
      d.posto_id === f.posto_id &&
      d.nome.trim().toLowerCase() === f.nome.trim().toLowerCase() &&
      d.tipo === f.tipo
    );

  const confirmarDesqualificacao = async () => {
    if (!motivoModal) return;
    const posto = postos.find(p => p.codigo === motivoModal.posto_codigo);
    if (!posto) return;
    try {
      await axios.post(`${API}/periodos/${id}/desqualificados`, {
        nome: motivoModal.nome,
        tipo: motivoModal.tipo,
        posto_codigo: motivoModal.posto_codigo,
        posto_id: posto.id,
        motivo: motivoTexto,
      });
      setMotivoModal(null);
      loadTodosFuncionarios();
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao desqualificar');
    }
  };

  const requalificar = async (f) => {
    const rec = getDesqRecord(f);
    if (!rec) return;
    try {
      await axios.delete(`${API}/periodos/${id}/desqualificados/${rec.id}`);
      loadTodosFuncionarios();
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao requalificar');
    }
  };

  // ── Tornar produto especial
  const tornarEspecial = (venda) => {
    const posto = postos.find(p => p.codigo === venda.posto_codigo);
    if (!posto) return;
    setProdVenda({ ...venda, posto });
    setProdForm({ comissao_frentista: '', comissao_trocador: '', comissao_gerente: '' });
    setProdError('');
    setShowProdModal(true);
  };

  const salvarProdEspecial = async () => {
    setSavingProd(true); setProdError('');
    try {
      await axios.post(`${API}/postos/${prodVenda.posto.id}/produtos-especiais`, {
        nome_produto: prodVenda.produto,
        comissao_frentista: prodForm.comissao_frentista || 0,
        comissao_trocador: prodForm.comissao_trocador || 0,
        comissao_gerente: prodForm.comissao_gerente || 0,
      });
      setShowProdModal(false);
      loadProdutosEspeciais();
    } catch (e) { setProdError(e.response?.data?.error || 'Erro ao salvar produto'); }
    finally { setSavingProd(false); }
  };

  const isProdEspecial = (venda) => {
    const posto = postos.find(p => p.codigo === venda.posto_codigo);
    if (!posto) return false;
    return produtosEspeciais.some(
      pe => pe.posto_id === posto.id &&
            pe.nome_produto.trim().toLowerCase() === venda.produto.trim().toLowerCase() &&
            pe.ativo
    );
  };

  const tipoColor = t => t === 'gerente' ? 'badge-blue' : t === 'trocador' ? 'badge-amber' : 'badge-gray';

  const diasInfo = (() => {
    if (!periodo || periodo.status === 'fechado') return null;
    const ini = new Date(periodo.data_inicio);
    const fim = new Date(periodo.data_fim);
    const hoje = new Date();
    const total = Math.round((fim - ini) / 86400000) + 1;
    const ref = hoje < fim ? hoje : fim;
    const corridos = Math.max(1, Math.round((ref - ini) / 86400000) + 1);
    return { total, corridos, fator: corridos / total };
  })();

  const funcDesqFiltrados = filterDesqPosto
    ? todosFuncionarios.filter(f => f.posto_codigo === filterDesqPosto)
    : todosFuncionarios;

  if (loading) return (
    <>
      <div className="topbar"><div className="topbar-title">Carregando…</div></div>
      <div className="page"><Spinner /></div>
    </>
  );

  return (
    <>
      <div className="topbar">
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2, cursor: 'pointer' }}
            onClick={() => navigate('/periodos')}>← Períodos</div>
          <div className="topbar-title">{periodo?.nome}</div>
          <div className="topbar-sub mono">
            {new Date(periodo?.data_inicio).toLocaleDateString('pt-BR')} → {new Date(periodo?.data_fim).toLocaleDateString('pt-BR')}
            {' · '}
            <span className={`badge ${periodo?.status === 'ativo' ? 'badge-green' : 'badge-gray'}`}>
              {periodo?.status === 'ativo' ? 'Em aberto' : 'Fechado'}
            </span>
            {diasInfo && (
              <span style={{ marginLeft: 8, color: 'var(--amber)', fontSize: 11 }}>
                Pro rata: {diasInfo.corridos}/{diasInfo.total} dias ({(diasInfo.fator * 100).toFixed(1)}%)
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-8">
          {isAdmin && (
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(true)}>
              🗑 Excluir Período
            </button>
          )}
          {isAdmin && periodo?.status === 'ativo' && (
            <button className="btn btn-ghost btn-sm" onClick={async () => {
              await axios.put(`${API}/periodos/${id}`, { status: 'fechado' });
              load();
            }}>
              🔒 Fechar Período
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/comissoes?periodo=' + id)}>
            Ver Premiações →
          </button>
        </div>
      </div>

      <div className="page">
        {/* Tabs */}
        <div className="tabs mb-4" style={{ marginBottom: 16, display: 'inline-flex', flexWrap: 'wrap' }}>
          {[
            ['metas', 'Metas'],
            ['funcionarios', 'Gerentes & Trocadores'],
            ['importar', 'Importar Vendas'],
            ['vendas', 'Vendas'],
            ['desqualificados', 'Desqualificados'],
            ...(isAdmin ? [['metas-auto', '🎯 Metas Trimestrais']] : []),
          ].map(([k, v]) => (
            <div key={k} className={`tab ${tab === k ? 'on' : ''}`} onClick={() => setTab(k)}>{v}</div>
          ))}
        </div>

        {/* ─── METAS ─── */}
        {tab === 'metas' && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Metas por Posto</div>
                <div className="card-sub">
                  Meta frentista e trocador = valor individual por colaborador.
                  Meta do posto = base para premiação do gerente.
                  {diasInfo && <span style={{ color: 'var(--amber)', marginLeft: 6 }}>
                    Metas efetivas com pro rata de {(diasInfo.fator * 100).toFixed(1)}% ({diasInfo.corridos}/{diasInfo.total} dias).
                  </span>}
                </div>
              </div>
              {isAdmin && (
                <button className="btn btn-primary btn-sm" onClick={() => {
                  setEditMeta(null);
                  setMetaForm({ posto_id: '', meta_frentista: '', meta_trocador: '', meta_posto: '' });
                  setShowMetaModal(true);
                }}>+ Definir Meta</button>
              )}
            </div>
            {!metas.length ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                Nenhuma meta definida.{' '}
                {isAdmin && <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => setShowMetaModal(true)}>Definir agora →</span>}
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Posto</th><th>Nome</th>
                      <th className="text-right">Meta Frentista/un</th>
                      {diasInfo && <th className="text-right">Efetiva Frentista</th>}
                      <th className="text-right">Meta Trocador/un</th>
                      {diasInfo && <th className="text-right">Efetiva Trocador</th>}
                      <th className="text-right">Meta Posto</th>
                      {diasInfo && <th className="text-right">Efetiva Posto</th>}
                      {isAdmin && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {metas.map(m => (
                      <tr key={m.id}>
                        <td><span className="badge badge-gray mono">{m.codigo}</span></td>
                        <td>{m.posto_nome}</td>
                        <td className="text-right mono bold">{fmt(m.meta_frentista)}</td>
                        {diasInfo && <td className="text-right mono" style={{ color: 'var(--amber)' }}>{fmt(m.meta_frentista * diasInfo.fator)}</td>}
                        <td className="text-right mono bold">{fmt(m.meta_trocador)}</td>
                        {diasInfo && <td className="text-right mono" style={{ color: 'var(--amber)' }}>{fmt(m.meta_trocador * diasInfo.fator)}</td>}
                        <td className="text-right mono bold">{fmt(m.meta_posto)}</td>
                        {diasInfo && <td className="text-right mono" style={{ color: 'var(--amber)' }}>{fmt(m.meta_posto * diasInfo.fator)}</td>}
                        {isAdmin && (
                          <td>
                            <button className="btn btn-ghost btn-sm" onClick={() => {
                              setEditMeta(m);
                              setMetaForm({ posto_id: m.posto_id, meta_frentista: m.meta_frentista, meta_trocador: m.meta_trocador, meta_posto: m.meta_posto });
                              setShowMetaModal(true);
                            }}>Editar</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ─── GERENTES & TROCADORES ─── */}
        {tab === 'funcionarios' && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Gerentes & Trocadores deste Período</div>
                <div className="card-sub">
                  O nome deve ser <strong>exatamente igual</strong> à coluna B da planilha.
                  Use o tipo <strong>Ambos</strong> para quem acumula gerente + trocador.
                </div>
              </div>
              {isAdmin && (
                <button className="btn btn-primary btn-sm" onClick={() => {
                  setEditFunc(null);
                  setFuncForm({ posto_id: '', nome: '', tipo: 'trocador' });
                  setFuncError('');
                  setShowFuncModal(true);
                }}>+ Adicionar</button>
              )}
            </div>
            {!funcionarios.length ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                Nenhum gerente ou trocador cadastrado. Os demais serão classificados como <strong>frentistas</strong>.
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Posto</th><th>Nome</th><th>Tipo</th>{isAdmin && <th></th>}</tr></thead>
                  <tbody>
                    {funcionarios.map(f => (
                      <tr key={f.id}>
                        <td><span className="badge badge-gray mono">{f.codigo}</span></td>
                        <td className="mono" style={{ fontSize: 12 }}>{f.nome}</td>
                        <td><span className={`badge ${tipoColor(f.tipo)}`}>{f.tipo}</span></td>
                        {isAdmin && (
                          <td>
                            <div className="flex gap-8">
                              <button className="btn btn-ghost btn-sm" onClick={() => {
                                setEditFunc(f);
                                setFuncForm({ posto_id: f.posto_id, nome: f.nome, tipo: f.tipo });
                                setFuncError('');
                                setShowFuncModal(true);
                              }}>Editar</button>
                              <button className="btn btn-danger btn-sm" onClick={() => setDeleteFunc(f)}>Remover</button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ─── IMPORTAR ─── */}
        {tab === 'importar' && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Importar Vendas via CSV</div>
                <div className="card-sub">B=Chave Empresa · M=Funcionário · N=Produto · O=Qtde · P=Vl.Unit · Q=Vl.Bruto · R=Desconto · S=Acréscimo · T=Vl.Final</div>
              </div>
            </div>
            <div className="card-body">
              {periodo?.status === 'fechado' ? (
                <div className="alert alert-error">Período fechado — importação bloqueada.</div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Arquivos CSV</label>
                    <input
                      type="file"
                      accept=".csv"
                      multiple
                      disabled={importing}
                      onChange={e => { if (!importing && e.target.files.length) { doImportCsv(e.target.files); e.target.value = ''; } }}
                    />
                    <div className="form-hint">
                      Coluna B deve conter a <strong>Chave Empresa</strong> cadastrada no posto.
                      Os dados anteriores serão substituídos.
                    </div>
                  </div>
                  {importing && <div className="alert" style={{ background: 'var(--surface2)' }}>⟳ Importando…</div>}
                  {importResult && (
                    <div className={`alert ${importResult.ok ? 'alert-success' : 'alert-error'}`}>{importResult.msg}</div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ─── VENDAS ─── */}
        {tab === 'vendas' && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Vendas Importadas</div>
                <div className="card-sub">{vendasTotal} registros no total</div>
              </div>
              <div className="filters" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {isAdmin && periodo?.status !== 'fechado' && (
                  <button className="btn btn-primary btn-sm" onClick={() => {
                    setVendaManualPosto('');
                    setVendaManualLinhas([linhaVazia()]);
                    setVendaManualErro('');
                    setShowVendaManual(true);
                  }}>
                    ➕ Venda Manual
                  </button>
                )}
                <select value={filterPosto} onChange={e => { setFilterPosto(e.target.value); setPage(1); }} style={{ maxWidth: 180 }}>
                  <option value="">Todos os postos</option>
                  {postos.map(p => <option key={p.id} value={p.id}>{p.codigo} — {p.nome}</option>)}
                </select>
                <input
                  placeholder="🔍 Funcionário ou produto…"
                  value={filterFunc}
                  onChange={e => { setFilterFunc(e.target.value); setPage(1); }}
                  style={{ maxWidth: 220 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Por página:</span>
                  <select value={pageSize} onChange={e => { setPageSize(parseInt(e.target.value)); setPage(1); }} style={{ maxWidth: 90 }}>
                    {[50, 100, 200, 500, 1000, 2000, 5000].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Posto</th><th>Funcionário</th><th>Tipo</th><th>Produto</th>
                    <th className="text-right">Qtde</th><th className="text-right">Vl. Final</th>
                    {isAdmin && <th>Ação</th>}
                  </tr>
                </thead>
                <tbody>
                  {vendas.map((v, i) => {
                    const especial = isProdEspecial(v);
                    return (
                      <tr key={i} style={especial ? { background: 'rgba(245,158,11,0.05)' } : {}}>
                        <td><span className="badge badge-gray mono">{v.posto_codigo}</span></td>
                        <td style={{ fontSize: 12 }}>{v.funcionario}</td>
                        <td><span className={`badge ${tipoColor(v.tipo_funcionario)}`}>{v.tipo_funcionario}</span></td>
                        <td style={{ fontSize: 12, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {especial && <span style={{ color: 'var(--amber)', marginRight: 4 }}>★</span>}
                          {v.produto}
                          {especial && <span className="badge badge-amber" style={{ marginLeft: 6, fontSize: 10 }}>especial</span>}
                        </td>
                        {/* FIX: fmtQ converte '2.000' → '2', '39.900' → '39,9' */}
                        <td className="text-right mono">{fmtQ(v.quantidade)}</td>
                        <td className="text-right mono bold">{fmt(v.valor_final)}</td>
                        {isAdmin && (
                          <td>
                            <div className="flex gap-8" style={{ alignItems: 'center' }}>
                              {!especial ? (
                                <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => tornarEspecial(v)}>
                                  ★ Tornar especial
                                </button>
                              ) : (
                                <span style={{ fontSize: 11, color: 'var(--amber)' }}>★ Especial</span>
                              )}
                              {periodo?.status !== 'fechado' && (
                                <button
                                  className="btn btn-danger btn-sm"
                                  style={{ fontSize: 11, padding: '2px 7px' }}
                                  onClick={() => deleteVenda(v.id)}
                                  title="Remover venda"
                                >✕</button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {!vendas.length && (
                    <tr><td colSpan={isAdmin ? 7 : 6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                      Nenhuma venda encontrada.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {vendasTotal > pageSize && (
              <div className="pagination">
                <div className="pag-info">
                  Mostrando {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, vendasTotal)} de {vendasTotal}
                </div>
                <div className="pag-btns">
                  <button className="pag-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                  {Array.from({ length: Math.min(5, Math.ceil(vendasTotal / pageSize)) }, (_, i) => {
                    const pg = i + 1;
                    return <button key={pg} className={`pag-btn ${page === pg ? 'on' : ''}`} onClick={() => setPage(pg)}>{pg}</button>;
                  })}
                  {Math.ceil(vendasTotal / pageSize) > 5 && <span style={{ color: 'var(--text-muted)', padding: '0 4px' }}>…</span>}
                  <button className="pag-btn" disabled={page * pageSize >= vendasTotal} onClick={() => setPage(p => p + 1)}>›</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── DESQUALIFICADOS ─── */}
        {tab === 'desqualificados' && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Desqualificados</div>
                <div className="card-sub">
                  Funcionários desqualificados têm premiação zerada no relatório.
                  O motivo fica visível no relatório de premiações.
                </div>
              </div>
              <select value={filterDesqPosto} onChange={e => setFilterDesqPosto(e.target.value)} style={{ maxWidth: 200 }}>
                <option value="">Todos os postos</option>
                {postos.map(p => <option key={p.id} value={p.codigo}>{p.codigo} — {p.nome}</option>)}
              </select>
            </div>

            {loadingDesq ? <Spinner text="Carregando funcionários…" /> : (
              <>
                {desqualificados.length > 0 && (
                  <div style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.06)', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--red)' }}>
                    ⚠️ {desqualificados.length} funcionário(s) desqualificado(s) — premiações zeradas no relatório
                  </div>
                )}
                {funcDesqFiltrados.length === 0 ? (
                  <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                    Nenhum funcionário encontrado. Importe vendas ou cadastre funcionários primeiro.
                  </div>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Posto</th><th>Funcionário</th><th>Tipo</th><th>Status</th><th>Motivo</th>
                          {isAdmin && <th>Ação</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {funcDesqFiltrados.map((f, i) => {
                          const desq = isDesqualificado(f);
                          const rec = getDesqRecord(f);
                          return (
                            <tr key={i} style={desq ? { background: 'rgba(239,68,68,0.05)' } : {}}>
                              <td><span className="badge badge-gray mono">{f.posto_codigo}</span></td>
                              <td style={{ fontWeight: 500 }}>{f.nome}</td>
                              <td><span className={`badge ${tipoColor(f.tipo)}`}>{f.tipo}</span></td>
                              <td>
                                {desq
                                  ? <span className="badge badge-red">Desqualificado</span>
                                  : <span className="badge badge-green">Qualificado</span>}
                              </td>
                              <td style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 220 }}>
                                {rec?.motivo || <span style={{ color: 'var(--border-light)' }}>—</span>}
                              </td>
                              {isAdmin && (
                                <td>
                                  {desq ? (
                                    <button className="btn btn-ghost btn-sm" onClick={() => requalificar(f)}>
                                      Requalificar
                                    </button>
                                  ) : (
                                    <button className="btn btn-danger btn-sm" onClick={() => {
                                      setMotivoModal({ nome: f.nome, tipo: f.tipo, posto_codigo: f.posto_codigo });
                                      setMotivoTexto('');
                                    }}>
                                      Desqualificar
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─── METAS TRIMESTRAIS ─── */}
        {tab === 'metas-auto' && (
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, alignItems: 'start' }}>

            {/* Coluna esquerda: configuração */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="card">
                <div className="card-header"><div className="card-title">Períodos de Referência</div></div>
                <div style={{ padding: '10px 14px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                    Selecione os períodos para calcular a média (recomendado: últimos 3)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 260, overflowY: 'auto' }}>
                    {allPeriodos.map(p => (
                      <label key={p.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                        padding: '5px 8px', borderRadius: 7, fontSize: 12,
                        background: fonteIds.includes(p.id) ? 'var(--accent-soft)' : 'var(--surface2)',
                        border: `1px solid ${fonteIds.includes(p.id) ? 'var(--accent)' : 'var(--border)'}`,
                      }}>
                        <input type="checkbox" checked={fonteIds.includes(p.id)}
                          onChange={() => {
                            setFonteIds(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]);
                            setPreviewRows(null); setAutoMetaResult(null);
                          }} />
                        <span style={{ flex: 1 }}>{p.nome}</span>
                        <span className={`badge ${p.status === 'ativo' ? 'badge-green' : 'badge-gray'}`}
                          style={{ fontSize: 9 }}>{p.status}</span>
                      </label>
                    ))}
                    {allPeriodos.length === 0 && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Carregando períodos…</div>
                    )}
                  </div>
                  {fonteIds.length > 0 && (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--accent)', fontWeight: 700 }}>
                      {fonteIds.length} período(s) selecionado(s)
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header"><div className="card-title">Ajuste %</div></div>
                <div style={{ padding: '10px 14px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="number" className="input" min={0} max={100} step={0.5}
                      value={percentualAuto}
                      onChange={e => { setPercentualAuto(parseFloat(e.target.value) || 0); setPreviewRows(null); }}
                      style={{ width: 80 }}
                    />
                    <span style={{ fontWeight: 700 }}>%</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>sobre a média</span>
                  </div>
                </div>
              </div>

              <button className="btn btn-primary" style={{ width: '100%' }}
                disabled={!fonteIds.length || loadingAutoMeta}
                onClick={calcAutoPreview}>
                {loadingAutoMeta ? '⟳ Calculando...' : '📊 Calcular Preview'}
              </button>

              {erroAutoMeta && (
                <div className="alert alert-error" style={{ fontSize: 12 }}>{erroAutoMeta}</div>
              )}

              {previewRows && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button className="btn btn-primary" style={{ width: '100%' }}
                    disabled={applyingAutoMeta} onClick={() => aplicarAutoMetas(false)}>
                    {applyingAutoMeta ? '⟳ Aplicando...' : '✅ Aplicar Metas'}
                  </button>
                  <button className="btn" style={{ width: '100%', background: '#25d366', color: '#fff', border: 'none' }}
                    disabled={applyingAutoMeta} onClick={() => aplicarAutoMetas(true)}>
                    {applyingAutoMeta ? '⟳ Aplicando...' : '📱 Aplicar + Notificar WPP'}
                  </button>
                </div>
              )}

              {autoMetaResult && (
                <div className={`alert ${autoMetaResult.ok ? 'alert-success' : 'alert-error'}`} style={{ fontSize: 12 }}>
                  {autoMetaResult.ok ? autoMetaResult.data.message : autoMetaResult.msg}
                  {autoMetaResult.ok && autoMetaResult.data.whatsapp?.length > 0 && (
                    <ul style={{ marginTop: 6, paddingLeft: 14 }}>
                      {autoMetaResult.data.whatsapp.map(w => (
                        <li key={w.posto} style={{ color: w.status === 'enviado' ? '#22c55e' : '#ef4444' }}>
                          {w.posto}: {w.status === 'enviado' ? '✓ enviado' : `✗ ${w.erro}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Coluna direita: tabela editável */}
            {!previewRows && !loadingAutoMeta && (
              <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🎯</div>
                <div style={{ fontWeight: 700 }}>Selecione os períodos e clique em Calcular Preview</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  Os valores calculados poderão ser ajustados antes de aplicar.
                </div>
              </div>
            )}

            {loadingAutoMeta && <div className="card" style={{ padding: 48, textAlign: 'center' }}><Spinner /></div>}

            {previewRows && !loadingAutoMeta && (
              <div className="card">
                <div className="card-header">
                  <div>
                    <div className="card-title">Preview — edite os valores antes de aplicar</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Média do realizado em {fonteIds.length} período(s) + {percentualAuto}% • Clique nos valores para editar
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm no-print" onClick={() => window.print()}>
                    🖨️ Exportar PDF
                  </button>
                </div>
                <div className="table-wrap">
                  <table style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Posto</th>
                        <th className="text-right" style={{ color: 'var(--text-muted)' }}>Realiz./Frent.</th>
                        <th className="text-right" style={{ color: 'var(--accent)' }}>Meta Frent. (+{percentualAuto}%)</th>
                        <th className="text-right" style={{ color: 'var(--text-muted)' }}>Realiz./Troc.</th>
                        <th className="text-right" style={{ color: 'var(--accent)' }}>Meta Troc. (+{percentualAuto}%)</th>
                        <th className="text-right" style={{ color: 'var(--text-muted)' }}>Total Posto</th>
                        <th className="text-right" style={{ color: 'var(--accent)' }}>Meta Posto (+{percentualAuto}%)</th>
                        <th style={{ width: 32, textAlign: 'center' }}>WPP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map(r => (
                        <tr key={r.posto_id}>
                          <td>
                            <div style={{ fontWeight: 700 }}>{r.codigo}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{r.posto_nome}</div>
                          </td>
                          <td className="text-right mono" style={{ color: 'var(--text-muted)' }}>{fmt(r.avg_frentista)}</td>
                          <td className="text-right">
                            <input type="number" step="0.01" min="0"
                              value={r.nova_frentista}
                              onChange={e => editPreviewRow(r.posto_id, 'nova_frentista', e.target.value)}
                              style={{ width: 100, textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12,
                                border: '1px solid var(--accent)', borderRadius: 4, padding: '2px 6px',
                                background: 'var(--surface)', color: 'var(--accent)', fontWeight: 700 }}
                            />
                          </td>
                          <td className="text-right mono" style={{ color: 'var(--text-muted)' }}>{fmt(r.avg_trocador)}</td>
                          <td className="text-right">
                            <input type="number" step="0.01" min="0"
                              value={r.nova_trocador}
                              onChange={e => editPreviewRow(r.posto_id, 'nova_trocador', e.target.value)}
                              style={{ width: 100, textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12,
                                border: '1px solid var(--accent)', borderRadius: 4, padding: '2px 6px',
                                background: 'var(--surface)', color: 'var(--accent)', fontWeight: 700 }}
                            />
                          </td>
                          <td className="text-right mono" style={{ color: 'var(--text-muted)' }}>{fmt(r.avg_posto)}</td>
                          <td className="text-right">
                            <input type="number" step="0.01" min="0"
                              value={r.nova_posto}
                              onChange={e => editPreviewRow(r.posto_id, 'nova_posto', e.target.value)}
                              style={{ width: 110, textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12,
                                border: '1px solid var(--accent)', borderRadius: 4, padding: '2px 6px',
                                background: 'var(--surface)', color: 'var(--accent)', fontWeight: 700 }}
                            />
                          </td>
                          <td style={{ textAlign: 'center', fontSize: 14 }}>
                            {r.tem_whatsapp ? '✅' : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td style={{ fontWeight: 800 }}>TOTAL</td>
                        <td className="text-right mono" style={{ color: 'var(--text-muted)' }}>
                          {fmt(previewRows.reduce((s, r) => s + (parseFloat(r.avg_frentista) || 0), 0))}
                        </td>
                        <td className="text-right mono" style={{ fontWeight: 800 }}>
                          {fmt(previewRows.reduce((s, r) => s + (parseFloat(r.nova_frentista) || 0), 0))}
                        </td>
                        <td className="text-right mono" style={{ color: 'var(--text-muted)' }}>
                          {fmt(previewRows.reduce((s, r) => s + (parseFloat(r.avg_trocador) || 0), 0))}
                        </td>
                        <td className="text-right mono" style={{ fontWeight: 800 }}>
                          {fmt(previewRows.reduce((s, r) => s + (parseFloat(r.nova_trocador) || 0), 0))}
                        </td>
                        <td className="text-right mono" style={{ color: 'var(--text-muted)' }}>
                          {fmt(previewRows.reduce((s, r) => s + (parseFloat(r.avg_posto) || 0), 0))}
                        </td>
                        <td className="text-right mono" style={{ fontWeight: 800 }}>
                          {fmt(previewRows.reduce((s, r) => s + (parseFloat(r.nova_posto) || 0), 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Relatório PDF Metas Trimestrais (print-only) ── */}
      {previewRows && (
        <div className="print-only" style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#000', padding: 0 }}>
          {/* Cabeçalho */}
          <div style={{ borderBottom: '2px solid #1e293b', paddingBottom: 10, marginBottom: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>Relatório — Metas Trimestrais</div>
            <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
              <strong>Período destino:</strong> {periodo?.nome} &nbsp;|&nbsp;
              <strong>Ajuste:</strong> +{percentualAuto}% &nbsp;|&nbsp;
              <strong>Referências:</strong> {fonteIds.map(fid => nomesPeriodos[fid] || `#${fid}`).join(' · ')}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
              Gerado em {new Date().toLocaleString('pt-BR')}
            </div>
          </div>

          {/* Tabela por posto */}
          {previewRows.map(r => (
            <div key={r.posto_id} style={{ marginBottom: 18, pageBreakInside: 'avoid' }}>
              <div style={{ background: '#1e293b', color: '#fff', padding: '4px 10px', fontWeight: 700, fontSize: 12, borderRadius: '4px 4px 0 0' }}>
                {r.codigo} — {r.posto_nome}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #cbd5e1' }}>Indicador</th>
                    {fonteIds.map(fid => (
                      <th key={fid} style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1' }}>
                        {nomesPeriodos[fid] || `#${fid}`}
                      </th>
                    ))}
                    <th style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', background: '#e2e8f0' }}>Média</th>
                    <th style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', background: '#dbeafe', color: '#1d4ed8', fontWeight: 800 }}>
                      Nova Meta (+{percentualAuto}%)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Linha Frentista */}
                  <tr>
                    <td style={{ padding: '4px 8px', border: '1px solid #cbd5e1', fontWeight: 600 }}>👷 Por Frentista</td>
                    {fonteIds.map(fid => {
                      const d = r.detalhe?.find(x => String(x.periodo_id) === String(fid));
                      return (
                        <td key={fid} style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', fontFamily: 'monospace' }}>
                          {d?.per_frent != null ? `R$ ${Number(d.per_frent).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'}
                          {d?.qtd_frent > 0 && <div style={{ fontSize: 9, color: '#888' }}>{d.qtd_frent} frent.</div>}
                        </td>
                      );
                    })}
                    <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', background: '#f8fafc', fontFamily: 'monospace' }}>
                      R$ {Number(r.avg_frentista).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', background: '#eff6ff', fontWeight: 800, fontFamily: 'monospace', color: '#1d4ed8' }}>
                      R$ {Number(r.nova_frentista).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                  {/* Linha Trocador */}
                  <tr>
                    <td style={{ padding: '4px 8px', border: '1px solid #cbd5e1', fontWeight: 600 }}>🔧 Por Trocador</td>
                    {fonteIds.map(fid => {
                      const d = r.detalhe?.find(x => String(x.periodo_id) === String(fid));
                      return (
                        <td key={fid} style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', fontFamily: 'monospace' }}>
                          {d?.per_troc != null ? `R$ ${Number(d.per_troc).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'}
                          {d?.qtd_troc > 0 && <div style={{ fontSize: 9, color: '#888' }}>{d.qtd_troc} troc.</div>}
                        </td>
                      );
                    })}
                    <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', background: '#f8fafc', fontFamily: 'monospace' }}>
                      R$ {Number(r.avg_trocador).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', background: '#eff6ff', fontWeight: 800, fontFamily: 'monospace', color: '#1d4ed8' }}>
                      R$ {Number(r.nova_trocador).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                  {/* Linha Posto */}
                  <tr style={{ background: '#f8fafc' }}>
                    <td style={{ padding: '4px 8px', border: '1px solid #cbd5e1', fontWeight: 600 }}>🏪 Total Posto</td>
                    {fonteIds.map(fid => {
                      const d = r.detalhe?.find(x => String(x.periodo_id) === String(fid));
                      return (
                        <td key={fid} style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', fontFamily: 'monospace' }}>
                          {d ? `R$ ${Number(d.total_posto).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'}
                        </td>
                      );
                    })}
                    <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', background: '#f1f5f9', fontFamily: 'monospace' }}>
                      R$ {Number(r.avg_posto).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #cbd5e1', background: '#eff6ff', fontWeight: 800, fontFamily: 'monospace', color: '#1d4ed8' }}>
                      R$ {Number(r.nova_posto).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}

          {/* Rodapé */}
          <div style={{ marginTop: 20, borderTop: '1px solid #cbd5e1', paddingTop: 8, fontSize: 10, color: '#888' }}>
            Cálculo: média do realizado (valor_final) em cada período + {percentualAuto}% de ajuste.
            Frentistas e Trocadores: total vendido ÷ quantidade de colaboradores distintos no período.
          </div>
        </div>
      )}

      {/* ── Modais ── */}

      {showMetaModal && (
        <Modal title={editMeta ? 'Editar Meta' : 'Definir Meta do Posto'} onClose={() => setShowMetaModal(false)}>
          <div className="modal-body">
            <div className="form-group">
              <label>Posto</label>
              <select value={metaForm.posto_id} onChange={e => setMetaForm({ ...metaForm, posto_id: e.target.value })} disabled={!!editMeta}>
                <option value="">Selecione…</option>
                {postos.map(p => <option key={p.id} value={p.id}>{p.codigo} — {p.nome}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Meta Frentista (R$/colaborador)</label>
                <input type="number" min="0" step="0.01" placeholder="0,00" value={metaForm.meta_frentista}
                  onChange={e => setMetaForm({ ...metaForm, meta_frentista: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Meta Trocador (R$/colaborador)</label>
                <input type="number" min="0" step="0.01" placeholder="0,00" value={metaForm.meta_trocador}
                  onChange={e => setMetaForm({ ...metaForm, meta_trocador: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Meta do Posto — base do gerente (R$ total)</label>
              <input type="number" min="0" step="0.01" placeholder="0,00" value={metaForm.meta_posto}
                onChange={e => setMetaForm({ ...metaForm, meta_posto: e.target.value })} />
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-ghost" onClick={() => setShowMetaModal(false)}>Cancelar</button>
            <button className="btn btn-primary" disabled={savingMeta || !metaForm.posto_id} onClick={saveMeta}>
              {savingMeta ? 'Salvando…' : 'Salvar Meta'}
            </button>
          </div>
        </Modal>
      )}

      {showFuncModal && (
        <Modal title={editFunc ? 'Editar Gerente/Trocador' : 'Adicionar Gerente ou Trocador'} onClose={() => setShowFuncModal(false)}>
          <div className="modal-body">
            {funcError && <div className="alert alert-error">{funcError}</div>}
            <div className="alert alert-info" style={{ marginBottom: 14, fontSize: 12 }}>
              O nome deve ser <strong>exatamente igual</strong> ao da coluna B da planilha.<br />
              Use <strong>Ambos</strong> para quem é gerente e também aparece como trocador nas vendas.
            </div>
            <div className="form-group">
              <label>Posto</label>
              <select value={funcForm.posto_id} onChange={e => setFuncForm({ ...funcForm, posto_id: e.target.value })} disabled={!!editFunc}>
                <option value="">Selecione…</option>
                {postos.map(p => <option key={p.id} value={p.id}>{p.codigo} — {p.nome}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Nome (exato como na planilha)</label>
              <input placeholder="Ex: 000006 - CARLOS ROBERTO ALVES" value={funcForm.nome}
                onChange={e => setFuncForm({ ...funcForm, nome: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Tipo</label>
              <select value={funcForm.tipo} onChange={e => setFuncForm({ ...funcForm, tipo: e.target.value })}>
                <option value="gerente">Gerente</option>
                <option value="trocador">Trocador de Óleo</option>
                <option value="ambos">Ambos (Gerente + Trocador)</option>
              </select>
              {funcForm.tipo === 'ambos' && (
                <div className="form-hint" style={{ color: 'var(--amber)' }}>
                  ⚡ Premiação gerencial (soma frentistas + trocadores + 3% posto se meta) + premiação de trocador própria
                </div>
              )}
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-ghost" onClick={() => setShowFuncModal(false)}>Cancelar</button>
            <button className="btn btn-primary" disabled={savingFunc || !funcForm.nome || !funcForm.posto_id} onClick={saveFunc}>
              {savingFunc ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </Modal>
      )}

      {deleteFunc && (
        <ConfirmModal
          title="Remover Gerente/Trocador"
          message={`Remover "${deleteFunc.nome}" (${deleteFunc.tipo}) deste período?`}
          onConfirm={doDeleteFunc}
          onClose={() => setDeleteFunc(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Excluir Período"
          message={`Tem certeza que deseja excluir "${periodo?.nome}"? Todos os dados (vendas, metas, funcionários) serão removidos permanentemente.`}
          onConfirm={doDelete}
          onClose={() => setConfirmDelete(false)}
        />
      )}

      {motivoModal && (
        <Modal title={`Desqualificar: ${motivoModal.nome}`} onClose={() => setMotivoModal(null)}>
          <div className="modal-body">
            <div className="form-group">
              <label>Motivo da desqualificação</label>
              <textarea
                rows={3}
                placeholder="Ex: Afastamento por licença, desligamento…"
                value={motivoTexto}
                onChange={e => setMotivoTexto(e.target.value)}
                style={{ resize: 'vertical' }}
              />
              <div className="form-hint">Opcional — ficará visível no relatório de premiações.</div>
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-ghost" onClick={() => setMotivoModal(null)}>Cancelar</button>
            <button className="btn btn-danger" onClick={confirmarDesqualificacao}>
              Confirmar Desqualificação
            </button>
          </div>
        </Modal>
      )}

      {showVendaManual && (
        <Modal title="Adicionar Vendas Manualmente" onClose={() => setShowVendaManual(false)}>
          <div className="modal-body">
            {vendaManualErro && <div className="alert alert-error" style={{ marginBottom: 12 }}>{vendaManualErro}</div>}

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Posto</label>
              <select value={vendaManualPosto} onChange={e => setVendaManualPosto(e.target.value)}>
                <option value="">Selecione o posto…</option>
                {postos.map(p => <option key={p.id} value={p.id}>{p.codigo} — {p.nome}</option>)}
              </select>
            </div>

            <datalist id="dl-funcionarios">
              {funcionarios.map((f, i) => <option key={i} value={f.nome} />)}
            </datalist>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--surface2)' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Funcionário</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Tipo</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Produto</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, minWidth: 70 }}>Qtde</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, minWidth: 90 }}>Vl. Final (R$)</th>
                    <th style={{ width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {vendaManualLinhas.map((l, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '4px 6px' }}>
                        <input
                          list="dl-funcionarios"
                          placeholder="Nome do funcionário"
                          value={l.funcionario}
                          onChange={e => setVendaManualLinhas(prev => prev.map((x, j) => j === i ? { ...x, funcionario: e.target.value } : x))}
                          style={{ width: '100%', minWidth: 150 }}
                        />
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <select
                          value={l.tipo}
                          onChange={e => setVendaManualLinhas(prev => prev.map((x, j) => j === i ? { ...x, tipo: e.target.value } : x))}
                          style={{ width: '100%' }}
                        >
                          <option value="frentista">Frentista</option>
                          <option value="trocador">Trocador</option>
                          <option value="gerente">Gerente</option>
                        </select>
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <input
                          placeholder="Produto"
                          value={l.produto}
                          onChange={e => setVendaManualLinhas(prev => prev.map((x, j) => j === i ? { ...x, produto: e.target.value } : x))}
                          style={{ width: '100%', minWidth: 130 }}
                        />
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <input
                          type="number" min="0.001" step="0.001" placeholder="1"
                          value={l.quantidade}
                          onChange={e => setVendaManualLinhas(prev => prev.map((x, j) => j === i ? { ...x, quantidade: e.target.value } : x))}
                          style={{ width: '100%', textAlign: 'right' }}
                        />
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <input
                          type="number" min="0" step="0.01" placeholder="0,00"
                          value={l.valor_final}
                          onChange={e => setVendaManualLinhas(prev => prev.map((x, j) => j === i ? { ...x, valor_final: e.target.value } : x))}
                          style={{ width: '100%', textAlign: 'right' }}
                        />
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        {vendaManualLinhas.length > 1 && (
                          <button
                            className="btn btn-danger btn-sm"
                            style={{ padding: '2px 7px', fontSize: 13 }}
                            onClick={() => setVendaManualLinhas(prev => prev.filter((_, j) => j !== i))}
                          >✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              className="btn btn-ghost btn-sm"
              style={{ marginTop: 10 }}
              onClick={() => setVendaManualLinhas(prev => [...prev, linhaVazia()])}
            >
              + Adicionar linha
            </button>
          </div>
          <div className="modal-foot">
            <button className="btn btn-ghost" onClick={() => setShowVendaManual(false)}>Cancelar</button>
            <button
              className="btn btn-primary"
              disabled={savingVendaManual || !vendaManualPosto}
              onClick={saveVendaManual}
            >
              {savingVendaManual ? 'Salvando…' : `Inserir ${vendaManualLinhas.filter(l => l.funcionario.trim() && l.produto.trim() && parseFloat(l.valor_final) > 0).length || 0} venda(s)`}
            </button>
          </div>
        </Modal>
      )}

      {showProdModal && prodVenda && (
        <Modal title="Tornar Produto Especial" onClose={() => setShowProdModal(false)}>
          <div className="modal-body">
            {prodError && <div className="alert alert-error">{prodError}</div>}
            <div style={{ padding: '10px 14px', background: 'var(--surface2)', borderRadius: 'var(--radius)', marginBottom: 16, fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-dim)', marginBottom: 4 }}>Produto</div>
              <div style={{ color: 'var(--amber)', wordBreak: 'break-word' }}>{prodVenda.produto}</div>
              <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Posto: <strong>{prodVenda.posto?.codigo} — {prodVenda.posto?.nome}</strong></div>
            </div>
            <div className="alert alert-info" style={{ fontSize: 12, marginBottom: 14 }}>
              Valor de premiação por unidade vendida para cada tipo. Use <strong>0</strong> para não pagar premiação especial àquele tipo.
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Premiação Frentista (R$/un)</label>
                <input type="number" min="0" step="0.01" placeholder="0,00" value={prodForm.comissao_frentista}
                  onChange={e => setProdForm({ ...prodForm, comissao_frentista: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Premiação Trocador (R$/un)</label>
                <input type="number" min="0" step="0.01" placeholder="0,00" value={prodForm.comissao_trocador}
                  onChange={e => setProdForm({ ...prodForm, comissao_trocador: e.target.value })} />
              </div>
            </div>
            <div className="form-group" style={{ maxWidth: '50%' }}>
              <label>Premiação Gerente (R$/un)</label>
              <input type="number" min="0" step="0.01" placeholder="0,00" value={prodForm.comissao_gerente}
                onChange={e => setProdForm({ ...prodForm, comissao_gerente: e.target.value })} />
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-ghost" onClick={() => setShowProdModal(false)}>Cancelar</button>
            <button className="btn btn-primary" disabled={savingProd} onClick={salvarProdEspecial}>
              {savingProd ? 'Salvando…' : '★ Salvar como Especial'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
